import { useState, type ReactElement } from 'react';
import { Circle } from 'lucide-react';
import { coerceToUtcDate } from '@/lib/date-utils';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';

interface LastSeenFreshnessCellProps {
  lastSeenAt: string | Date;
  /** Optional reference timestamp (ms). When omitted, captures Date.now()
   *  on mount via useState initializer (compiler-safe). Cells receive fresh
   *  instances on each data refetch so the timestamp stays current. */
  nowMs?: number;
}

/**
 * Shared inventory "Last Seen" cell: last-seen time with a color-coded
 * freshness dot. Used by every inventory table (MCP servers, Extensions,
 * Plugins) so the column reads identically across the Inventory page.
 *
 * Dot tiers:
 * - Green:  seen within 1 day   — actively used
 * - Amber:  seen within 7 days  — recent but not today
 * - Orange: seen within 14 days — going stale
 * - Red:    not seen for >14 days — likely inactive
 */
export function LastSeenFreshnessCell({
  lastSeenAt,
  nowMs,
}: LastSeenFreshnessCellProps): ReactElement {
  // useState initializer is exempt from React Compiler purity rules.
  // TanStack Table creates fresh cell instances on data refetch, so the
  // captured timestamp stays current without needing an effect.
  const [mountNow] = useState(Date.now);
  const now = nowMs ?? mountNow;
  // Parse with the SAME UTC normalization the formatters use, so the dot tier
  // and the relative label can't disagree on a timezone-less backend string.
  // Unparseable input → Infinity days → the red "likely inactive" tier.
  const parsedDate = coerceToUtcDate(lastSeenAt);
  const daysSinceLastSeen = parsedDate
    ? Math.floor((now - parsedDate.getTime()) / (1000 * 60 * 60 * 24))
    : Infinity;

  let dotColor: string;
  if (daysSinceLastSeen <= 1) {
    dotColor = 'fill-green-500 text-green-500';
  } else if (daysSinceLastSeen <= 7) {
    dotColor = 'fill-amber-400 text-amber-400';
  } else if (daysSinceLastSeen <= 14) {
    dotColor = 'fill-orange-400 text-orange-400';
  } else {
    dotColor = 'fill-red-400 text-red-400';
  }

  // Inline text + opposite-form tooltip + Timestamp Format handling all live in
  // the shared RelativeTimeWithTooltip (single source of truth — no local
  // copy of that logic to drift). The freshness dot is injected as the leading
  // glyph; the formatters there read the raw `lastSeenAt` as UTC, matching the
  // dot's `coerceToUtcDate` parse above.
  return (
    <RelativeTimeWithTooltip
      date={lastSeenAt}
      showIcon={false}
      leading={
        <Circle
          data-testid="last-seen-freshness-dot"
          className={`h-2 w-2 shrink-0 ${dotColor}`}
        />
      }
    />
  );
}
